import React, { useState, useEffect } from 'react';
import { FaVideo, FaCog, FaArrowRight, FaTimes, FaChevronUp, FaExclamationTriangle } from 'react-icons/fa';

const ProctoringCoachMarks = ({ 
  isVisible, 
  onDismiss, 
  isCameraOn, 
  onOpenSettings,
  devices = { cameras: [], mics: [] },
  selectedCamera = '',
  onCameraChange
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Reset step when visibility changes
  useEffect(() => {
    if (isVisible) {
      setCurrentStep(0);
      setDismissed(false);
    }
  }, [isVisible]);

  // Auto-advance to camera step if camera is off
  useEffect(() => {
    if (isVisible && !isCameraOn && currentStep === 0) {
      setCurrentStep(1);
    }
  }, [isVisible, isCameraOn, currentStep]);

  const handleDismiss = () => {
    setDismissed(true);
    setTimeout(() => {
      onDismiss();
    }, 300); // Match the transition duration
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleDismiss();
    }
  };

  const handleOpenSettings = () => {
    onOpenSettings();
    handleNextStep();
  };
  
  const handleCameraSelection = (e) => {
    if (onCameraChange) {
      onCameraChange(e);
    }
  };

  const steps = [
    {
      title: "Let's get started!",
      content: "This is your proctoring room where your camera feed will be monitored during the interview.",
      action: () => handleNextStep(),
      actionText: "Next",
      position: "center"
    },
    {
      title: "Front Camera Required",
      content: "Your front camera must be enabled for the interview. Please select your front-facing camera from the options below and make sure your face is clearly visible.",
      action: isCameraOn ? () => handleNextStep() : () => handleOpenSettings(),
      actionText: isCameraOn ? "Next" : "Enable Camera",
      position: "bottom",
      highlight: "camera"
    },
    // {
    //   title: "Front Camera Required",
    //   content: "If needed, you can change your camera using the settings button at the bottom of the screen.",
    //   action: () => handleOpenSettings(),
    //   actionText: "Open Settings",
    //   position: "bottom",
    //   highlight: "settings"
    // },
    {
      title: "You're All Set",
      content: "Your camera is now active. You're ready for the interview. Good luck!",
      action: () => handleDismiss(),
      actionText: "Got it",
      position: "center"
    }
  ];

  const currentStepData = steps[currentStep];

  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-end justify-center transition-opacity duration-300 ${dismissed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
      {/* Overlay for clicking outside */}
      <div className="absolute inset-0 bg-black/70" onClick={handleDismiss}></div>
      
      {/* Bottom Sheet Coach Mark */}
      <div 
        className={`relative bg-white rounded-t-2xl shadow-xl w-full transition-all duration-300 min-h-[70vh] max-h-[90vh] flex flex-col ${dismissed ? 'translate-y-full' : 'translate-y-0'}`}
      >
        {/* Header with handle bar, progress steps and close button */}
        <div className="border-b border-gray-200 sticky top-0 bg-white z-10">
          {/* Handle bar for bottom sheet */}
          {/* <div className="w-full flex justify-center pt-3">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
          </div> */}
          
          {/* Step indicators at top */}
          <div className="w-full flex justify-center space-x-3 py-3">
            {steps.map((_, index) => (
              <div 
                key={index} 
                className={`h-1.5 transition-all duration-300 ${currentStep === index ? 'w-8 bg-blue-600' : 'w-2 bg-gray-300'} rounded-full`}
              ></div>
            ))}
          </div>
          
          {/* Close button */}
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <FaTimes size={20} />
          </button>
          
          {/* Title with icon */}
          <div className="px-6 pt-2 pb-4">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              {/* <FaChevronUp className="mr-2 text-blue-500" /> */}
              {currentStepData.title}
            </h3>
          </div>
        </div>
        
        {/* Content area - scrollable */}
        <div className="flex-grow overflow-y-auto px-6 py-4">
          <p className="text-gray-700 mb-6">{currentStepData.content}</p>
        
          {/* Highlight element if needed */}
          {currentStepData.highlight === 'camera' && (
            <div className="mb-6">
              {/* Camera status indicator */}
              <div className="flex items-center mb-4 p-4 bg-blue-50 rounded-lg">
                <FaVideo size={32} className={isCameraOn ? "text-green-500" : "text-red-500"} />
                <span className="ml-3 font-medium">{isCameraOn ? "Camera is active" : "Camera is not active"}</span>
              </div>
              
              {/* Camera selection dropdown */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">Select your front-facing camera:</label>
                <select
                  value={selectedCamera}
                  onChange={handleCameraSelection}
                  className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" disabled>Select a camera</option>
                  {devices.cameras.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Camera guidance */}
              {!isCameraOn && (
                <div className="flex items-start p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                  <FaExclamationTriangle className="text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-gray-700">
                    If your camera isn't showing, please select a different camera from the dropdown or check your browser permissions.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {currentStepData.highlight === 'settings' && (
            <div className="flex items-center justify-center mb-6 p-4 bg-blue-50 rounded-lg">
              <FaCog size={32} className="text-blue-500 animate-pulse" />
              <span className="ml-3 font-medium">Click the settings icon to change cameras</span>
            </div>
          )}
        </div>
        
        {/* Action buttons - sticky at bottom */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end space-x-3 shadow-lg">
          <button 
            onClick={handleDismiss}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Skip
          </button>
          <button 
            onClick={currentStepData.action}
            className="px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 flex items-center font-medium"
          >
            {currentStepData.actionText}
            <FaArrowRight className="ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProctoringCoachMarks;
